// Canvas-based image compressor for the Contact Us screenshot upload.
// Mirrors the patient app's behavior: long edge ≤ 1920 px, JPEG q=0.8,
// rejects anything still over 5 MB after compression.

const MAX_LONG_EDGE = 1920;
const MAX_OUT_BYTES = 5 * 1024 * 1024;
const JPEG_QUALITY = 0.8;

export interface CompressedImage {
  blob: Blob;
  filename: string;
  mimeType: string;
}

function scale(inWidth: number, inHeight: number): { width: number; height: number } {
  const longEdge = Math.max(inWidth, inHeight);
  if (longEdge <= MAX_LONG_EDGE) {
    return { width: inWidth, height: inHeight };
  }
  const ratio = MAX_LONG_EDGE / longEdge;
  return {
    width: Math.round(inWidth * ratio),
    height: Math.round(inHeight * ratio),
  };
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  // Fallback for older browsers — should be unreachable in our supported set.
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read the selected image.'));
    img.src = URL.createObjectURL(file);
  });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Could not encode the screenshot.'));
        }
      },
      'image/jpeg',
      quality,
    );
  });
}

export async function compressImage(file: File): Promise<CompressedImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed.');
  }

  const bitmap = await loadBitmap(file);
  const { width, height } = scale(bitmap.width, bitmap.height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not prepare image for upload. Try a different file.');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);

  const blob = await canvasToJpegBlob(canvas, JPEG_QUALITY);
  if (blob.size > MAX_OUT_BYTES) {
    throw new Error('Screenshot is too large even after compression (limit 5 MB).');
  }

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'screenshot';
  return {
    blob,
    filename: `${baseName}.jpg`,
    mimeType: 'image/jpeg',
  };
}
